/**
 * Large-screen (>1920px viewport) layout helpers for the revamped pages.
 *
 * The revamp designs are authored on a 1920 frame and all desktop sizing is
 * rem-anchored (1rem = 0.833vw below 1920, capped at 16px above — see the html
 * rule in tailwind.css). On wider screens content therefore stops growing;
 * these helpers centre it in a 1920 container while backgrounds and carousels
 * stay full-bleed. `120rem` equals ~100vw for lg…1920 (so the cap never binds
 * there) and freezes at 1920px above — every helper is a visual no-op at
 * ≤1920.
 *
 * Three treatments:
 * - `CONTENT_CAP` — plain content sections: centre the inner content, keep the
 *   section itself full-width for edge-to-edge backgrounds.
 * - `OVERLAY_CAP` — absolute overlays on full-bleed media (hero titles/logos):
 *   the media spans the viewport while overlay offsets anchor to the container.
 * - `BLEED_GUTTER_*` / `BLEED_BREAKOUT_RIGHT` — full-bleed carousels and
 *   scroll strips whose first item must align with the container gutter while
 *   the track runs to the physical viewport edge (padding on the scroll
 *   viewport, so overflow clips at the padding box and items stay visible
 *   through the gutter as they scroll).
 *
 * The gutter helpers are single `md:` utilities with a CSS `max()` rather than
 * a base class plus a `qhd:` override: a stacked override of the same property
 * competes on stylesheet order with the base (`md:` can win past 1921px), while
 * `max(3rem, …)` needs no second rule — it collapses to the plain 3rem gutter
 * at ≤1920 and tracks the centred container's edge above. `%` resolves against
 * the padded element's full-width parent; the negative-margin breakout has no
 * usable `%` base, so it uses 100vw — pair it with `overflow-x-clip` on an
 * ancestor so a classic (non-overlay) scrollbar's width can't cause a
 * horizontal page scroll.
 *
 * The cap inside the `max()` is the literal `1920px`, not `120rem`: below the
 * rem cap 1rem is 0.833vw, so 120rem is 99.96vw — just under 100vw. A `120rem`
 * cap therefore never lets the breakout collapse to a clean 3rem, leaving it
 * ~0.02vw wider than the `%`-based gutter it cancels at every desktop width,
 * which is enough for the browser to add a horizontal page scrollbar. `1920px`
 * equals 120rem exactly once the rem cap binds, so >1920 behaviour is
 * unchanged; below it both sides resolve to the same plain 3rem.
 */

/** Centred 1920 container for section content. */
export const CONTENT_CAP = 'mx-auto w-full max-w-[120rem]';

/**
 * Inner cap for content INSIDE a section that carries the 3rem gutter itself:
 * a plain 120rem cap would centre in the padded box and land content at the
 * container edge (losing the gutter) above 1920 — 114rem (= 120rem - 2 x 3rem)
 * keeps it at container + gutter. Hosts with other gutters inline the same
 * subtraction.
 */
export const CONTENT_CAP_PADDED = 'mx-auto w-full max-w-[114rem]';

/** Absolute-overlay variant of {@link CONTENT_CAP} for full-bleed media. */
export const OVERLAY_CAP = 'absolute inset-0 mx-auto w-full max-w-[120rem]';

/** 48px gutter that tracks the centred container's left edge on >1920 screens. */
export const BLEED_GUTTER_LEFT =
    'md:pl-[max(3rem,calc((100%_-_1920px)/2_+_3rem))]';

/** Both-side variant of {@link BLEED_GUTTER_LEFT}. */
export const BLEED_GUTTER_X =
    'md:px-[max(3rem,calc((100%_-_1920px)/2_+_3rem))]';

/**
 * Right-edge breakout for scroll strips inside a gutter-padded row: cancels
 * the row's gutter so the strip runs to the physical viewport edge. Replaces
 * `md:-mr-12` (which it equals at ≤1920).
 */
export const BLEED_BREAKOUT_RIGHT =
    'md:-mr-[max(3rem,calc((100vw_-_1920px)/2_+_3rem))]';

/**
 * Both-side variant of {@link BLEED_BREAKOUT_RIGHT} — cancels the row's gutter
 * on both edges so a carousel's slides scroll to the physical viewport edges.
 * Pair it with {@link BLEED_GUTTER_X} on the carousel viewport, which puts the
 * same distance back as padding so the first and last snaps stay aligned to the
 * gutter while the slides bleed through it.
 */
export const BLEED_BREAKOUT_X =
    'md:-mx-[max(3rem,calc((100vw_-_1920px)/2_+_3rem))]';

/**
 * Slide basis for a four-up carousel that starts at the container gutter and
 * runs off the right edge — pair it with {@link BLEED_BREAKOUT_RIGHT}.
 *
 * Such a track is one gutter wider than the content column its cards have to
 * match, so a plain quarter would run them wide. Taking the gutter off first
 * keeps the cards exactly the width of the grid's columns, and what is left
 * over at the edge is the fifth slide peeking in — the cue that the row
 * continues. `%` resolves against the track, so this stays correct whatever a
 * classic scrollbar takes off the viewport.
 */
export const BLEED_SLIDE_BASIS_4 =
    'md:basis-[calc((100%_-_max(3rem,calc((100vw_-_1920px)/2_+_3rem)))/4)]';
